import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { display, space } from 'styled-system';

import { Link } from 'common/components/Link';
import Ratio from 'common/components/Ratio';
import { BodyRegularBR2 } from 'common/components/Typography';
import type { WordBreakProps } from 'common/theme/system-utilities';
import { wordBreak } from 'common/theme/system-utilities';
import type { DyTrackingGA4Props } from 'common/tracking/getDyTrackingGA4Params';
import type { VisualNavigationItemData } from 'common/types/visual-navigation';
import { useTrackingEvents } from './hooks/tracking';
import SlideImage from './SlideImage';

interface VisualNavigationItemProps extends VisualNavigationItemData {
  index: number;
  itemsCount: number;
  withDataPrefetch: boolean;
  withBackground?: boolean;
  title?: string;
  tracking?: DyTrackingGA4Props;
  imageRatio?: number;
}

export const IMAGE_RATIO = 0.72;

const SlideContainer = styled(Link)<any>`
  ${display};
  ${space};
`;

const slideContainerStyles: ComponentProps<typeof SlideContainer> = {
  display: 'block',
};

const SlideDescription = styled(BodyRegularBR2)<WordBreakProps>`
  ${wordBreak};
`;

const slideDescriptionStyles: ComponentProps<typeof SlideDescription> = {
  as: 'p',
  textAlign: 'center',
  wordBreak: 'break-word',
};

export const VisualNavigationItem = ({
  link,
  description,
  image: imageProps,
  index,
  itemsCount,
  title,
  withDataPrefetch,
  tracking,
  withBackground,
  imageRatio,
}: VisualNavigationItemProps) => {
  const { onImageClick } = useTrackingEvents({
    linkText: description,
    listPosition: index,
    listLength: itemsCount,
    listItem: description,
    title,
    tracking,
  });

  return (
    <SlideContainer
      {...slideContainerStyles}
      href={link}
      onClick={onImageClick}
      data-testid={`visual-navigation-slide-${index}`}
      mr={withBackground ? ['sp_16', 'sp_24', null, null] : ['sp_16', '18px', 'sp_20', 'sp_16']}
      {...(withDataPrefetch ? { 'data-prefetch': true } : {})}
    >
      <Ratio ratio={imageRatio ?? IMAGE_RATIO}>
        <SlideImage imageProps={imageProps} index={index} alt="" />
      </Ratio>
      <SlideDescription {...slideDescriptionStyles} data-testid="description-slide">
        {description}
      </SlideDescription>
    </SlideContainer>
  );
};
